import { Anchor, Button, Card, Group, Select, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";
import { useMemo } from "react";
import { useSearchParams } from "react-router";
import {
  DataTable,
  EmptyState,
  FilterBar,
  PageHeader,
  StatCard,
  useSearchParamState,
} from "@how/ui";
import { env } from "../../lib/env";
import { isAuthorizationError } from "../../lib/query-client";
import {
  PERIODS,
  articleName,
  articleUrl,
  byLeastHelpful,
  helpfulShare,
  period,
  useHelpFeedback,
  type ArticleFeedback,
} from "./help-feedback-queries";

const NO_SORTING: never[] = [];

function share(value: number | null): string {
  return value === null ? "—" : `${String(value)}%`;
}

function columns(onPick: (slug: string) => void): ColumnDef<ArticleFeedback>[] {
  return [
    {
      accessorKey: "article",
      header: "Article",
      enableSorting: false,
      cell: (ctx) => (
        <Anchor
          href={articleUrl(env.VITE_HOW_PLATFORM_BASE_DOMAIN, ctx.row.original.article)}
          target="_blank"
          rel="noopener"
          size="sm"
          c="var(--how-text-primary)"
          underline="always"
          onClick={(event) => {
            event.stopPropagation();
          }}
          aria-label={`${articleName(ctx.row.original.article)} (opens the article in a new tab)`}
        >
          {articleName(ctx.row.original.article)}
        </Anchor>
      ),
    },
    {
      id: "share",
      header: "Helpful",
      enableSorting: false,
      meta: { numeric: true },
      cell: (ctx) => share(helpfulShare(ctx.row.original)),
    },
    {
      accessorKey: "helpful",
      header: "Yes",
      enableSorting: false,
      meta: { numeric: true },
    },
    {
      accessorKey: "notHelpful",
      header: "No",
      enableSorting: false,
      meta: { numeric: true },
    },
    {
      accessorKey: "comments",
      header: "Comments",
      enableSorting: false,
      meta: { numeric: true },
      cell: (ctx) =>
        ctx.row.original.comments === 0 ? (
          "0"
        ) : (
          <Button
            variant="subtle"
            size="compact-sm"
            onClick={(event) => {
              event.stopPropagation();
              onPick(ctx.row.original.article);
            }}
            aria-label={`Show the ${String(ctx.row.original.comments)} comments on ${articleName(ctx.row.original.article)}`}
          >
            {ctx.row.original.comments}
          </Button>
        ),
    },
    {
      accessorKey: "lastAt",
      header: "Last answer",
      enableSorting: false,
      meta: { numeric: true },
      cell: (ctx) => new Date(ctx.row.original.lastAt).toLocaleDateString(),
    },
  ];
}

/**
 * "Was this helpful?" from HoW's help center (ADR 0059 amendment, D27): every article answered in the period, least
 * helpful first, and the newest comments, which a row narrows to its article. Anonymous answers: no visitor, church or
 * login is stored with them. DCX Logic staff only, super admin and support alike, which the database enforces.
 */
export function HelpFeedbackPage() {
  const [days] = useSearchParamState("days", "90");
  const [article, setArticle] = useSearchParamState("article");
  const [, setParams] = useSearchParams();
  const chosen = period(days);
  const feedback = useHelpFeedback(chosen);
  const rows = useMemo(() => byLeastHelpful(feedback.data?.articles ?? []), [feedback.data]);
  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex: 0, pageSize: Math.max(rows.length, 1) }),
    [rows.length],
  );
  const status = feedback.isPending
    ? "loading"
    : feedback.isError
      ? isAuthorizationError(feedback.error)
        ? "denied"
        : "error"
      : "ready";
  const comments = (feedback.data?.comments ?? []).filter(
    (c) => article === "" || c.article === article,
  );
  const yes = rows.reduce((sum, r) => sum + r.helpful, 0);
  const no = rows.reduce((sum, r) => sum + r.notHelpful, 0);
  const pick = (slug: string) => {
    setArticle(slug);
  };

  return (
    <>
      <PageHeader
        title="Help feedback"
        description="What readers of the help center answered to “Was this article helpful?”, least helpful first, with what they said was missing. Answers are anonymous: no visitor, church or login is kept with them."
      />
      <FilterBar
        filters={
          <Select
            aria-label="Period"
            data={Object.entries(PERIODS).map(([value, label]) => ({ value, label }))}
            value={chosen}
            allowDeselect={false}
            onChange={(value) => {
              setParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  if (value === null || value === "90") next.delete("days");
                  else next.set("days", value);
                  return next;
                },
                { replace: true },
              );
            }}
            w={{ base: "100%", sm: 200 }}
          />
        }
      />
      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="lg">
        <StatCard label="Answers" value={yes + no} loading={feedback.isPending} />
        <StatCard
          label="Found it helpful"
          value={share(helpfulShare({ helpful: yes, notHelpful: no }))}
          hint={`${String(yes)} yes, ${String(no)} no`}
          loading={feedback.isPending}
        />
        <StatCard label="Articles answered" value={rows.length} loading={feedback.isPending} />
      </SimpleGrid>
      <DataTable
        label="Articles by helpfulness"
        density="dense"
        minWidth={720}
        columns={columns(pick)}
        rows={rows}
        totalRows={rows.length}
        status={status}
        pagination={pagination}
        onPaginationChange={() => undefined}
        sorting={NO_SORTING}
        onSortingChange={() => undefined}
        getRowId={(row) => row.article}
        onRowClick={(row) => {
          pick(row.article);
        }}
        emptyTitle="No answers yet"
        emptyDescription="Nobody has answered “Was this article helpful?” in this period."
        errorMessage="Could not load help feedback."
        onRetry={() => {
          void feedback.refetch();
        }}
      />
      {status !== "ready" ? null : (
        <Stack gap="sm" mt="xl" component="section" aria-labelledby="help-feedback-comments">
          <Group justify="space-between" align="baseline">
            <Title order={2} size="h4" id="help-feedback-comments">
              {article === "" ? "Recent comments" : `Comments on ${articleName(article)}`}
            </Title>
            {article === "" ? null : (
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={() => {
                  setArticle(undefined);
                }}
              >
                Show every article
              </Button>
            )}
          </Group>
          {comments.length === 0 ? (
            <EmptyState
              title="No comments"
              description="A comment is the sentence a reader may add after answering No."
            />
          ) : (
            comments.map((c) => (
              <Card key={c.id} withBorder padding="md">
                <Stack gap={4}>
                  <Group justify="space-between" gap="xs">
                    <Text size="sm" fw={600}>
                      {articleName(c.article)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {new Date(c.createdAt).toLocaleString()}
                    </Text>
                  </Group>
                  <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                    {c.comment}
                  </Text>
                </Stack>
              </Card>
            ))
          )}
          <Text size="xs" c="dimmed">
            Comments are scrubbed of email addresses and telephone numbers before they are stored,
            and kept a year.
          </Text>
        </Stack>
      )}
    </>
  );
}
